import { request, jsonOptions, authSessionStore, uploadFormData } from './client';

export const authApi = {
  register: async (payload) => {
    const data = await request('/auth/register', jsonOptions('POST', payload));
    authSessionStore.set(data || {});
    return data;
  },
  // Login accepts an optional 2FA payload ({ twoFactorCode } or
  // { recoveryCode }) on the same call. The first call without
  // either throws ApiError code === 'TWO_FACTOR_REQUIRED'; the
  // login page catches that and prompts.
  // `captchaToken` / `nickname` (the honeypot) ride along when the
  // server has bot protection configured; both are stripped server-side
  // before the controller sees them.
  login: async (email, password, { twoFactorCode, recoveryCode, captchaToken, nickname } = {}) => {
    const data = await request('/auth/login', jsonOptions('POST', {
      email, password, twoFactorCode, recoveryCode, captchaToken, nickname
    }));
    authSessionStore.set(data || {});
    return data;
  },
  me: () => request('/auth/me'),
  logout: async (refreshToken) => {
    try { await request('/auth/logout', jsonOptions('POST', { refreshToken })); }
    finally { authSessionStore.clear(); }
  },
  forgotPassword: (email, { captchaToken, nickname } = {}) => request('/auth/password/forgot', jsonOptions('POST', { email, captchaToken, nickname })),
  resetPassword: (token, password) => request('/auth/password/reset', jsonOptions('POST', { token, password })),
  requestEmailVerification: () => request('/auth/email/verify/request', { method: 'POST' }),
  verifyEmail: (token) => request('/auth/email/verify', jsonOptions('POST', { token })),
  updateProfile: (payload) => request('/auth/profile', { method: 'PATCH', body: JSON.stringify(payload) }),
  updatePreferences: (payload) => request('/me/preferences', { method: 'PATCH', body: JSON.stringify(payload) }),
  uploadAvatar: (file, onProgress) => {
    const form = new FormData();
    form.append('avatar', file);
    return uploadFormData('/auth/avatar', form, onProgress);
  },
  usernameAvailability: (username) => request(`/auth/username-availability?username=${encodeURIComponent(username)}`),
  emailAvailability: (email) => request(`/auth/email-availability?email=${encodeURIComponent(email)}`),

  // 2FA — three channels share one setup → enable → live flow.
  //   method='totp'  → response: { secret, otpauthUri, recoveryCodes }
  //   method='email' → response: { maskedTarget, recoveryCodes }
  //   method='sms'   → response: { maskedTarget, recoveryCodes }
  setupTwoFactor:          ({ method = 'totp', phoneNumber } = {}) =>
    request('/auth/2fa/setup', jsonOptions('POST', { method, phoneNumber })),
  enableTwoFactor:         (code) => request('/auth/2fa/enable', jsonOptions('POST', { code })),
  disableTwoFactor:        ({ code, recoveryCode } = {}) => request('/auth/2fa/disable', jsonOptions('POST', { code, recoveryCode })),
  regenerateRecoveryCodes: ({ code, recoveryCode } = {}) =>
    request('/auth/2fa/recovery-codes', jsonOptions('POST', { code, recoveryCode })),
  resendTwoFactorChallenge: (email, password) =>
    request('/auth/2fa/resend', jsonOptions('POST', { email, password })),

  // Sessions
  listSessions:  () => request('/auth/sessions'),
  revokeSession: (id) => request(`/auth/sessions/${id}`, { method: 'DELETE' })
};
